/**
 * Net-worth tracking. Snapshots live on profile.netWorthHistory (one
 * per UTC day, capped at 365). The store appends a snapshot on first
 * cold-start of each new day.
 *
 * Asset / liability mapping from Account.type:
 *   'checking' → checking (asset)
 *   'savings'  → savings (asset)
 *   'credit'   → creditCards (liability — subtracted)
 *
 * `investments`, `loans`, and `other` buckets exist on the snapshot
 * shape so the breakdown is forward-compatible — they stay 0 until
 * the Account.type union expands.
 */

import type { Account, NetWorthSnapshot } from '../data/types';

type Breakdown = NetWorthSnapshot['breakdown'];

export function computeNetWorth(
  accounts: Account[],
): Breakdown & { total: number } {
  const breakdown: Breakdown = {
    checking: 0,
    savings: 0,
    investments: 0,
    creditCards: 0,
    loans: 0,
    other: 0,
  };
  for (const a of accounts) {
    switch (a.type) {
      case 'checking':
        breakdown.checking += a.balance;
        break;
      case 'savings':
        breakdown.savings += a.balance;
        break;
      case 'credit':
        breakdown.creditCards += a.balance;
        break;
      // Future-proofing: any new account types fall through to `other`
      // until the mapping is extended.
      default:
        breakdown.other += a.balance;
        break;
    }
  }
  const total =
    breakdown.checking +
    breakdown.savings +
    breakdown.investments +
    breakdown.other -
    breakdown.creditCards -
    breakdown.loans;
  return {
    ...breakdown,
    total: Math.round(total * 100) / 100,
  };
}

export function snapshotNetWorth(
  accounts: Account[],
  today: string,
): NetWorthSnapshot {
  const r = computeNetWorth(accounts);
  return {
    date: today,
    total: r.total,
    breakdown: {
      checking: r.checking,
      savings: r.savings,
      investments: r.investments,
      creditCards: r.creditCards,
      loans: r.loans,
      other: r.other,
    },
  };
}

export function shouldSnapshotToday(
  history: NetWorthSnapshot[],
  today: string,
): boolean {
  if (history.length === 0) return true;
  return history[history.length - 1]!.date !== today;
}

/**
 * Append a snapshot to history. Same-day calls overwrite the prior
 * entry so intra-day balance edits don't grow the array. Caps at
 * `maxEntries`, dropping the oldest when needed.
 */
export function appendSnapshot(
  history: NetWorthSnapshot[],
  snap: NetWorthSnapshot,
  maxEntries: number = 365,
): NetWorthSnapshot[] {
  const last = history[history.length - 1];
  let next: NetWorthSnapshot[];
  if (last && last.date === snap.date) {
    next = [...history.slice(0, -1), snap];
  } else {
    next = [...history, snap];
  }
  if (next.length > maxEntries) {
    next = next.slice(next.length - maxEntries);
  }
  return next;
}
